/**
 * This is a wrapper component for different Layouts.
 * Navigational 'aside' content should be added to this wrapper.
 */
import { compose } from 'redux';
import React, { ReactElement } from 'react';
import { withViewport } from '../../util/contextHelpers';
import { FormattedMessage, LayoutWrapperSideNav, TypographyWrapper } from '..';
import { MAX_MEDIUM_SCREEN_WIDTH } from '../../util/window';
import { useCurrentUserPermissions } from '../../hooks/useUserPermissions';
import { useEnabledCustomerExperiences } from '../../hooks/useEnabledCustomerExperiences';
import { useSellerGeneratedLabels } from '../../hooks/useSellerGeneratedLabels';

export enum AccountSettingsTabId {
  ContactDetailsPage = 'ContactDetailsPage',
  PasswordChangePage = 'PasswordChangePage',
  StripePayoutPage = 'StripePayoutPage',
  PaymentMethodsPage = 'PaymentMethodsPage',
  SellerDetailsPage = 'SellerDetailsPage',
}

export type Tab = {
  text: ReactElement;
  selected: boolean;
  id: string;
  linkProps: {
    name: AccountSettingsTabId;
  };
};

const getTabId = (tabId: AccountSettingsTabId) => `${tabId}Tab`;

const scrollToTab = (currentTab: AccountSettingsTabId) => {
  const el = document.querySelector(`#${getTabId(currentTab)}`);

  if (el) {
    el.scrollIntoView({
      block: 'end',
      inline: 'end',
      behavior: 'smooth',
    });
  }
};

const LayoutWrapperAccountSettingsSideNavComponent = ({
  currentTab,
  viewport,
}: {
  currentTab: AccountSettingsTabId | null;
  viewport: {
    width: number;
    height: number;
  };
}): ReactElement => {
  const { allowBuy, allowMarketplace } = useEnabledCustomerExperiences();
  const { isBrand } = useCurrentUserPermissions();
  const isSellerGeneratedLabelEnabled = useSellerGeneratedLabels();

  let hasScrolledToTab = false;

  const { width } = viewport;
  const hasViewport = width > 0;
  const hasHorizontalTabLayout = hasViewport && width <= MAX_MEDIUM_SCREEN_WIDTH;
  const hasVerticalTabLayout = hasViewport && width > MAX_MEDIUM_SCREEN_WIDTH;
  const hasFontsLoaded = hasViewport && document.documentElement.classList.contains('fontsLoaded');

  // Check if scrollToTab call is needed (tab is not visible on mobile)
  if (hasVerticalTabLayout) {
    hasScrolledToTab = true;
  } else if (hasHorizontalTabLayout && currentTab && !hasScrolledToTab && hasFontsLoaded) {
    scrollToTab(currentTab);
    hasScrolledToTab = true;
  }

  const tabs: Tab[] = [
    {
      text: (
        <TypographyWrapper variant="h2">
          <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.contactDetailsTabTitle" />
        </TypographyWrapper>
      ),
      selected: currentTab === AccountSettingsTabId.ContactDetailsPage,
      id: getTabId(AccountSettingsTabId.ContactDetailsPage),
      linkProps: {
        name: AccountSettingsTabId.ContactDetailsPage,
      },
    },
    {
      text: (
        <TypographyWrapper variant="h2">
          <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.passwordTabTitle" />
        </TypographyWrapper>
      ),
      selected: currentTab === AccountSettingsTabId.PasswordChangePage,
      id: getTabId(AccountSettingsTabId.PasswordChangePage),
      linkProps: {
        name: AccountSettingsTabId.PasswordChangePage,
      },
    },
    ...(allowMarketplace || (allowBuy && isBrand)
      ? [
          {
            text: (
              <TypographyWrapper variant="h2">
                <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.paymentsTabTitle" />
              </TypographyWrapper>
            ),
            selected: currentTab === AccountSettingsTabId.StripePayoutPage,
            id: getTabId(AccountSettingsTabId.StripePayoutPage),
            linkProps: {
              name: AccountSettingsTabId.StripePayoutPage,
            },
          },
        ]
      : []),
    ...(allowBuy
      ? [
          {
            text: (
              <TypographyWrapper variant="h2">
                <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.paymentMethodsTabTitle" />
              </TypographyWrapper>
            ),
            selected: currentTab === AccountSettingsTabId.PaymentMethodsPage,
            id: getTabId(AccountSettingsTabId.PaymentMethodsPage),
            linkProps: {
              name: AccountSettingsTabId.PaymentMethodsPage,
            },
          },
        ]
      : []),
    ...(isSellerGeneratedLabelEnabled
      ? [
          {
            text: <TypographyWrapper variant="h2">Seller Details</TypographyWrapper>,
            selected: currentTab === AccountSettingsTabId.SellerDetailsPage,
            id: getTabId(AccountSettingsTabId.SellerDetailsPage),
            linkProps: {
              name: AccountSettingsTabId.SellerDetailsPage,
            },
          },
        ]
      : []),
  ];

  return <LayoutWrapperSideNav tabs={tabs} />;
};

const LayoutWrapperAccountSettingsSideNav = compose(withViewport)(
  LayoutWrapperAccountSettingsSideNavComponent
);

export default LayoutWrapperAccountSettingsSideNav;
